/** Canonical product / Product Bank image frame — 143 × 250, contain, white, 1px border. */

import { pickBestStillImageUrl, shoppableProductImageSrc } from "../media/feedMedia";

export const PRODUCT_IMAGE_WIDTH = 143;
export const PRODUCT_IMAGE_HEIGHT = 250;
export const PRODUCT_IMAGE_BACKGROUND = "#ffffff";
export const PRODUCT_IMAGE_BORDER = "1px solid #ddd";
export const PRODUCT_IMAGE_BORDER_RADIUS = 8;

export const PRODUCT_IMAGE_FRAME_CLASS = "product-bank-image-frame";
export const PRODUCT_IMAGE_FRAME_SQUARE_CLASS = "product-bank-image-frame--square";
export const PRODUCT_IMAGE_MEDIA_CLASS = "product-bank-image-frame__img";

export const PRODUCT_IMAGE_WIDTH_CSS = `${PRODUCT_IMAGE_WIDTH}px`;
export const PRODUCT_IMAGE_HEIGHT_CSS = `${PRODUCT_IMAGE_HEIGHT}px`;
export const PRODUCT_IMAGE_SQUARE_CSS = `${PRODUCT_IMAGE_WIDTH}px`;

/** True when carousel media shape is square (143 × 143). */
export function isProductImageSquare(
  shape?: string | boolean | null,
  className?: string,
): boolean {
  if (shape === true || shape === "sq" || shape === "square") return true;
  if (typeof className === "string") {
    return (
      className.includes("square-image") ||
      className.includes("square-wrapper") ||
      /\bsquare\b/.test(className)
    );
  }
  return false;
}

export type ProductImageSource = {
  type?: string | null;
  product_image?: string | null;
  media_url?: string | null;
  table_image?: string | null;
  platform?: string | null;
} | null | undefined;

/**
 * True only for posts whose own type is PRODUCT.
 * Do NOT treat carousel/component names (e.g. "product-bank", "trending products")
 * as a signal — those contexts can mix content + product posts.
 * Supports: shouldUseProductImageFrame(data) | shouldUseProductImageFrame(type, data)
 */
export function shouldUseProductImageFrame(
  typeOrData?: string | ProductImageSource | null,
  data?: ProductImageSource,
): boolean {
  let componentType = "";
  let post: ProductImageSource = undefined;

  if (typeof typeOrData === "string" || typeOrData == null) {
    componentType = String(typeOrData || "");
    post = data;
  } else {
    post = typeOrData;
    componentType = String(post?.type || "");
  }

  const postType = String(post?.type || "").toUpperCase();
  if (postType === "PRODUCT") return true;

  // First-arg alias when callers pass the post type string directly
  const normalized = componentType.toLowerCase();
  if (normalized === "product") return true;
  if (componentType.toUpperCase() === "PRODUCT") return true;

  return false;
}

/**
 * Product carousels (trending products, product-keyword / query-links, etc.)
 * often load shoppable FEED rows that omit `type: "PRODUCT"`. Tag them so
 * ProductBankImageFrame / contain+white CSS matches Custom Products (Product
 * Bank rows). Never tag "trending content".
 */
export function tagTrendingProductsAsProductType<T extends { type?: string | null }>(
  posts: T[] | null | undefined,
  carouselType?: string | null,
): T[] {
  if (!posts?.length) return posts || [];
  const kind = String(carouselType || "").toLowerCase().trim();
  if (kind === "trending content" || kind === "query-content" || kind === "content-series") {
    return posts;
  }
  return posts.map((post) =>
    String(post?.type || "").toUpperCase() === "PRODUCT"
      ? post
      : { ...post, type: "PRODUCT" },
  );
}

/** Alias — same PRODUCT-frame tagging for any product feed surface. */
export const tagProductFeedAsProductType = tagTrendingProductsAsProductType;

export {
  hasShoppableProductStill,
  shoppableProductImageSrc,
} from "../media/feedMedia";

/** Prefer durable stills — never return a video URL for <img> src. */
export function getProductImageSrc(data: ProductImageSource): string {
  if (!data) return "";
  return (
    shoppableProductImageSrc(data) ||
    pickBestStillImageUrl([
      data.product_image,
      data.table_image,
      data.media_url,
    ]) ||
    ""
  );
}
